import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { FeatureFlag } from '@environments/feature-flags';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { APP_HOME_URL } from '../auth/auth.state';

/**
 * Closes a route while its feature is switched off, sending anyone who guessed
 * the URL home instead. Use it on the route of any feature that has a route of
 * its own — hiding the nav item is not the same as closing the door, and a path
 * that still answers when typed is a feature that shipped.
 *
 * Belongs in `canMatch`, which the router evaluates while it is still deciding
 * which route the URL means — before the lazy chunk behind it is fetched. In
 * `canActivate` the chunk is already downloaded by the time the guard gets a
 * say, which hands the shape of an unreleased feature to anyone reading the
 * network tab.
 *
 * ```ts
 * {
 *   path: 'teams',
 *   canMatch: [featureFlagGuard('teams')],
 *   loadChildren: () => import('./features/teams/teams.routes'),
 * }
 * ```
 *
 * Either way the user lands on `redirectTo`: the `UrlTree` returned from
 * `canMatch` redirects rather than letting the URL fall through to the next
 * route that could have matched it (see the spec, which pins that down).
 */
export function featureFlagGuard(
  flag: FeatureFlag,
  redirectTo: string = APP_HOME_URL,
): CanActivateFn & CanMatchFn {
  return (): boolean | UrlTree => {
    if (inject(FeatureFlagsService).isEnabled(flag)) return true;
    return inject(Router).createUrlTree([redirectTo]);
  };
}
